// The Bluesky Show — the public network face of BlueHarbor.
// No login, no tracking: fetch the live directory, watch subscribe-only.

import './show.css'
import { Room, RoomEvent, Track, type RemoteParticipant, type RemoteTrack } from 'livekit-client'
import type { LiveStreamView } from '../../../shared/protocol'
import { didOfIdentity } from '../../../shared/ids'
import { makeSender, mountReactions, reactionBar } from './reactions'
import { mountLowerThird } from './lowerThird'

const app = document.getElementById('show')!
const CATEGORIES = ['all', 'talk', 'music', 'gaming', 'art', 'coding', 'irl']

// Same-origin in dev (vite proxy); absolute in production so the portal can
// be hosted anywhere (blueskyshow.com on Netlify, blueharbor.at, wherever).
const API = import.meta.env.DEV ? '' : 'https://blueharbor.at'

interface ReplaySegment {
  url: string
  title: string
  startedAt: number
  endedAt: number | null
}
interface ReplayShow {
  id: string
  title: string
  category: string
  harborName: string
  ownerDid: string
  poster?: string
  headerImg?: string
  statusText?: string
  hostBio?: string
  segments: ReplaySegment[]
}

// Channel-branded banner across the top of a watch/replay view. Skipped in
// rec mode so recordings stay full-bleed video.
// The host's own note for this channel — what's on, guests, links.
function statusEl(text: string | undefined): HTMLElement | null {
  if (!text || REC_MODE) return null
  const box = el('div', 'watch-status')
  box.append(el('span', 'watch-status-label', 'FROM THE HOST'), el('p', undefined, text))
  return box
}

function posterEl(path: string | undefined): HTMLElement | null {
  const img = el('img', 'tile-poster') as HTMLImageElement
  // No snapshot or channel art yet: the network card keeps tiles on-brand.
  img.src = path ? `${API}${path}` : DEFAULT_STANDBY
  img.alt = ''
  img.loading = 'lazy'
  return img
}

interface ScheduledShow {
  at: number
  durationMin: number
  title: string
  harborName: string
  channelName: string
  ownerDid: string
  streamId: string
  headerImg?: string
}

let streams: LiveStreamView[] = []
let replays: ReplayShow[] = []
let schedule: ScheduledShow[] = []
let category = 'all'
let watching: Room | null = null
// Replay mode must hold the screen exactly like live watching does — the
// 20s directory poll re-renders the home page over anything unguarded.
let replayActive = false
let replayCleanup: (() => void) | null = null

// Deep link (?watch=<stream id>) — where Bluesky LIVE badges land. Resolved
// against the directory once it loads; live wins, then today's replay.
const params = new URLSearchParams(location.search)
let pendingWatch: string | null = params.get('watch')

// Host page: /@handle — everything that host has on the network today.
// Two spellings: /@handle reads nicely, /host/handle survives clients that
// try to parse the @ as a mention. Whichever they arrived on, we keep.
const hostMatch = decodeURIComponent(location.pathname).match(/^\/(?:@|host\/)([^/]+)\/?$/)
let hostHandle: string | null = hostMatch?.[1] ?? null
const hostPrefix = location.pathname.startsWith('/host/') ? '/host/' : '/@'
let hostDid: string | null = null
let hostBio: string | null = null

// rec=1: chromeless mode for the recording egress — just the video, no UI.
const REC_MODE = params.get('rec') === '1'
if (REC_MODE) app.classList.add('rec-mode')

// Minted by the worker into the egress URL. Handed back when asking for a
// watch token, which is what keeps the recorder out of the viewer count — a
// viewer arriving without one is just a viewer.
const REC_KEY = params.get('rk')

// mute=1: program monitor. Same chromeless view, but silent — a host
// watching their own output would otherwise feed their speakers back into
// the call.
const MUTED = params.get('mute') === '1'
function applyMute(el: HTMLMediaElement): void {
  if (!MUTED) return
  el.muted = true
  el.volume = 0
}

// Away-moment fallback: channels without their own standby art get the
// network's card, so dead air still carries our branding.
const DEFAULT_STANDBY = '/og-image.jpg'

// Keep the viewer on the host page they arrived through.
function basePath(): string {
  return hostHandle ? `${hostPrefix}${hostHandle}/` : '/'
}

function standbyFor(s: { standbyImg?: string }): string {
  return s.standbyImg ? `${API}${s.standbyImg}` : DEFAULT_STANDBY
}

// ---- profiles (public AppView, tiny cache) ----
const profiles = new Map<string, { handle: string; displayName?: string; avatar?: string }>()
async function loadProfiles(dids: string[]): Promise<void> {
  const missing = [...new Set(dids)].filter((d) => d && !profiles.has(d))
  for (let i = 0; i < missing.length; i += 25) {
    const url = new URL('/xrpc/app.bsky.actor.getProfiles', 'https://public.api.bsky.app')
    for (const did of missing.slice(i, i + 25)) url.searchParams.append('actors', did)
    try {
      const res = await fetch(url)
      if (!res.ok) continue
      const data = (await res.json()) as { profiles: Array<{ did: string; handle: string; displayName?: string; avatar?: string }> }
      for (const p of data.profiles) profiles.set(p.did, p)
    } catch {
      // profile decoration is best-effort
    }
  }
}

// Share controls for whatever the viewer is looking at. Native share sheet
// on mobile, clipboard everywhere else, plus a one-tap Bluesky post.
// Corner share button for a directory tile — the tile itself is a "watch"
// button, so this one must not bubble.
function tileShare(url: string, text: string): HTMLElement {
  const btn = el('button', 'tile-share', '⤴')
  btn.title = 'Share this show'
  btn.onclick = async (e) => {
    e.stopPropagation()
    try {
      if (navigator.share) {
        await navigator.share({ title: text, url })
        return
      }
      await navigator.clipboard.writeText(url)
      btn.textContent = '✓'
      setTimeout(() => (btn.textContent = '⤴'), 1500)
    } catch {
      // cancelled
    }
  }
  return btn
}

function shareChips(url: string, text: string): HTMLElement[] {
  const share = el('button', 'show-chip', '⤴ Share')
  share.onclick = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: text, url })
        return
      }
      await navigator.clipboard.writeText(url)
      share.textContent = '✓ Link copied'
      setTimeout(() => (share.textContent = '⤴ Share'), 1800)
    } catch {
      // cancelled or blocked; nothing to report
    }
  }
  const post = el('a', 'show-chip', '🦋 Post') as HTMLAnchorElement
  post.href = `https://bsky.app/intent/compose?text=${encodeURIComponent(`${text} ${url}`)}`
  post.target = '_blank'
  post.rel = 'noopener'
  return [share, post]
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag)
  if (cls) n.className = cls
  if (text !== undefined) n.textContent = text
  return n
}

// Resolve /@handle to a DID once, so the directory can be filtered to them.
async function resolveHost(): Promise<void> {
  if (!hostHandle || hostDid) return
  try {
    const res = await fetch(
      `https://public.api.bsky.app/xrpc/app.bsky.actor.getProfile?actor=${encodeURIComponent(hostHandle)}`,
    )
    if (!res.ok) {
      hostHandle = null // unknown handle: fall back to the full network
      return
    }
    const p = (await res.json()) as { did: string; handle: string; displayName?: string; avatar?: string }
    hostDid = p.did
    profiles.set(p.did, p)

  } catch {
    hostHandle = null
  }
}

// ---- directory ----

async function refresh(): Promise<void> {
  try {
    await resolveHost()
    const res = await fetch(`${API}/api/live`)
    if (!res.ok) return
    streams = ((await res.json()) as { streams: LiveStreamView[] }).streams
    try {
      const rres = await fetch(`${API}/api/replays`)
      if (rres.ok) replays = ((await rres.json()) as { shows: ReplayShow[] }).shows
    } catch {
      // replays are decoration on the directory
    }
    try {
      const sres = await fetch(`${API}/api/schedule/week`)
      if (sres.ok) schedule = ((await sres.json()) as { shows: ScheduledShow[] }).shows
    } catch {
      // schedule is decoration too
    }
    await loadProfiles([
      ...streams.map((s) => s.ownerDid),
      ...replays.map((r) => r.ownerDid),
      ...schedule.map((s) => s.ownerDid),
    ])
    if (pendingWatch) {
      const wanted = streams.find((s) => s.id === pendingWatch)
      const replay = replays.find((r) => r.id === pendingWatch)
      pendingWatch = null
      if (wanted) {
        void watch(wanted)
        return
      }
      // The show ended, but its day-reel exists: play that instead of a shrug.
      if (replay) {
        watchReplay(replay)
        return
      }
    }
    render()
  } catch {
    // transient; next poll wins
  }
}

function render(): void {
  if (watching || replayActive) return // watch view owns the screen
  app.replaceChildren()

  const header = el('header', 'show-header')
  const brand = el('div', 'show-brand')
  brand.append(el('span', 'show-dot'), el('h1', undefined, 'THE BLUESKY SHOW'))
  header.append(brand, el('p', 'show-tag', 'Live, unscripted, straight from the AT Protocol.'))
  app.append(header)

  // Host page: the network filtered to one streamer, with their card on top.
  const host = hostDid ? profiles.get(hostDid) : null
  if (hostDid) {
    // Their own channel banner becomes the page's hero — live show first,
    // then today's newest replay.
    // The blurb belongs to a harbor, so take it from whatever of theirs is
    // on air, else their newest replay.
    hostBio =
      streams.find((s) => s.ownerDid === hostDid && s.hostBio)?.hostBio ??
      [...replays].reverse().find((r) => r.ownerDid === hostDid && r.hostBio)?.hostBio ??
      null
    const banner =
      streams.find((s) => s.ownerDid === hostDid && s.headerImg)?.headerImg ??
      [...replays].reverse().find((r) => r.ownerDid === hostDid && r.headerImg)?.headerImg
    const card = el('section', `host-card${banner ? ' has-banner' : ''}`)
    if (banner) {
      card.style.backgroundImage = `linear-gradient(to right, rgb(10 10 15 / 92%) 0%, rgb(10 10 15 / 72%) 55%, rgb(10 10 15 / 45%) 100%), url(${API}${banner})`
    }
    if (host?.avatar) {
      const img = el('img', 'host-avatar') as HTMLImageElement
      img.src = host.avatar
      img.alt = ''
      card.append(img)
    }
    const info = el('div', 'host-info')
    info.append(
      el('h2', undefined, host?.displayName || `@${host?.handle ?? hostHandle}`),
      el('p', 'host-handle', `@${host?.handle ?? hostHandle} · on The Bluesky Show`),
      ...(hostBio ? [el('p', 'host-bio', hostBio)] : []),
    )
    const links = el('div', 'host-links')
    const bsky = el('a', 'show-chip', '🦋 Follow on Bluesky') as HTMLAnchorElement
    bsky.href = `https://bsky.app/profile/${host?.handle ?? hostHandle}`
    bsky.target = '_blank'
    bsky.rel = 'noopener'
    const all = el('a', 'show-chip', '← All channels') as HTMLAnchorElement
    all.href = '/'
    links.append(
      all,
      bsky,
      ...shareChips(
        // @-free canonical: some clients mishandle an @ inside a URL path.
        `https://blueskyshow.com/host/${host?.handle ?? hostHandle}/`,
        `${host?.displayName || `@${host?.handle ?? hostHandle}`} on The Bluesky Show 📺`,
      ),
    )
    info.append(links)
    card.append(info)
    app.append(card)
  }

  const chips = el('nav', 'show-chips')
  for (const c of CATEGORIES) {
    const chip = el('button', `show-chip${category === c ? ' active' : ''}`, c === 'irl' ? 'IRL' : c === 'all' ? 'All channels' : c[0].toUpperCase() + c.slice(1))
    chip.onclick = () => {
      category = c
      render()
    }
    chips.append(chip)
  }
  app.append(chips)

  const mine = <T extends { ownerDid: string }>(x: T) => !hostDid || x.ownerDid === hostDid
  const visible = streams.filter((s) => (category === 'all' || s.category === category) && mine(s))
  const visibleReplays = replays.filter((r) => (category === 'all' || r.category === category) && mine(r))

  if (visible.length === 0 && visibleReplays.length === 0) {
    const empty = el('div', 'show-empty')
    empty.append(
      el('div', 'show-static'),
      el(
        'h2',
        undefined,
        hostDid ? 'Nothing from this host today' : 'Nothing on air right now',
      ),
      el(
        'p',
        undefined,
        hostDid
          ? 'Their shows appear here while they’re live, and replays stay up until the nightly sign-off.'
          : 'The harbor sleeps. Start your own broadcast from any harbor you own.',
      ),
      cta('Get a harbor on BlueHarbor'),
    )
    app.append(empty)
    return
  }

  if (visible.length > 0) {
    // Hero: most-watched.
    const hero = visible[0]
    const heroEl = el('section', 'show-hero')
    heroEl.onclick = () => watch(hero)
    const heroPoster = posterEl(hero.poster)
    if (heroPoster) heroEl.append(heroPoster)
    const heroInfo = el('div', 'show-hero-info')
    heroInfo.append(
      el('span', 'live-pill', '🔴 LIVE'),
      el('h2', undefined, hero.title),
      streamMeta(hero),
      el('button', 'show-watch-btn', '▶ Watch'),
    )
    heroEl.append(heroInfo)
    app.append(heroEl)

    // Grid.
    const grid = el('section', 'show-grid')
    for (const s of visible.slice(1)) {
      const tile = el('button', 'show-tile')
      tile.onclick = () => watch(s)
      const poster = posterEl(s.poster)
      if (poster) tile.append(poster)
      tile.append(
        el('span', 'live-pill live-pill-sm', 'LIVE'),
        el('h3', undefined, s.title),
        streamMeta(s),
        tileShare(`https://blueskyshow.com/?watch=${s.id}`, `🔴 Live now: ${s.title}`),
      )
      grid.append(tile)
    }
    if (visible.length > 1) app.append(grid)
  }

  // Today's reels: shows that already aired, replayable until midnight.
  if (visibleReplays.length > 0) {
    const row = el('div', 'show-section-row')
    row.append(
      el('h2', 'show-section-title', 'TODAY ON THE NETWORK'),
      el('span', 'reel-countdown', `⏳ Sign-off in ${signOffCountdown()} — replays clear every night`),
    )
    app.append(row)
    const rgrid = el('section', 'show-grid')
    for (const r of visibleReplays) {
      const tile = el('button', 'show-tile')
      tile.onclick = () => watchReplay(r)
      if (r.segments.length > 1) {
        tile.title = r.segments
          .map((s, i) => `${i + 1}. ${s.title} (${new Date(s.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })})`)
          .join('\n')
      }
      const poster = posterEl(r.poster)
      if (poster) tile.append(poster)
      tile.append(
        el('span', 'live-pill live-pill-sm replay-pill', 'REPLAY'),
        el('h3', undefined, r.title),
        replayMeta(r),
        tileShare(`https://blueskyshow.com/?watch=${r.id}`, `📺 ${r.title} — today on The Bluesky Show`),
      )
      rgrid.append(tile)
    }
    app.append(rgrid)
  }

  // The week ahead: appointment TV. Times render in the viewer's own zone.
  const mySchedule = schedule.filter((s) => !hostDid || s.ownerDid === hostDid)
  if (mySchedule.length > 0) {
    const schedHeader = el('div', 'show-section-row')
    schedHeader.append(
      el('h2', 'show-section-title', hostDid ? 'WEEKLY SCHEDULE' : 'THIS WEEK ON THE NETWORK'),
      el('span', 'reel-countdown', 'times shown in your timezone'),
    )
    app.append(schedHeader)
    const byDay = new Map<string, ScheduledShow[]>()
    for (const show of mySchedule) {
      const key = new Date(show.at).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })
      const list = byDay.get(key) ?? []
      list.push(show)
      byDay.set(key, list)
    }
    const sched = el('section', 'sched-list')
    for (const [dayLabel, shows] of byDay) {
      const dayEl = el('div', 'sched-day')
      dayEl.append(el('div', 'sched-day-label', dayLabel))
      for (const show of shows) {
        const p = profiles.get(show.ownerDid)
        const row = el('div', 'sched-row')
        const time = new Date(show.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        const soon = show.at - Date.now() < 3600_000
        // On the air right now? The directory is the source of truth.
        const liveNow = streams.find((x) => x.id === show.streamId)
        const replayOf = replays.find((x) => x.id === show.streamId)
        row.append(
          el('span', `sched-time${soon ? ' soon' : ''}`, time),
          el('span', 'sched-title', show.title),
          ...(liveNow ? [el('span', 'live-pill live-pill-sm', '🔴 ON AIR')] : []),
          el('span', 'sched-host', p ? `@${p.handle}` : show.harborName),
        )
        const watch_ = el(
          'button',
          `sched-watch${liveNow ? ' live' : ''}`,
          liveNow ? '▶ Watch live' : replayOf ? '▶ Watch replay' : 'Watch the show',
        )
        watch_.onclick = () => {
          if (liveNow) void watch(liveNow)
          else if (replayOf) watchReplay(replayOf)
          else location.href = `${basePath()}?watch=${encodeURIComponent(show.streamId)}`
        }
        row.append(watch_)
        dayEl.append(row)
      }
      sched.append(dayEl)
    }
    app.append(sched)
  }

  const foot = el('footer', 'show-foot')
  foot.append(cta('Join the conversation on BlueHarbor'))
  app.append(foot)
}

// Time until the network's nightly sign-off (4am America/Chicago, past
// midnight coast to coast — the retention boundary; keep in sync with the
// worker's dayStartMs). A countdown reads true in every timezone.
function signOffCountdown(): string {
  const now = new Date()
  const wall = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' }))
  const since = wall.getHours() * 3600_000 + wall.getMinutes() * 60_000 + wall.getSeconds() * 1000
  const sinceSignOff = (since - 4 * 3600_000 + 86_400_000) % 86_400_000
  const left = 86_400_000 - sinceSignOff
  const h = Math.floor(left / 3600_000)
  const m = Math.floor((left % 3600_000) / 60_000)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

function replayMeta(r: ReplayShow, link = false): HTMLElement {
  const meta = el('div', 'show-meta')
  const mins = Math.round(
    r.segments.reduce((total, s) => total + ((s.endedAt ?? s.startedAt) - s.startedAt), 0) / 60000,
  )
  meta.append(
    ...hostChip(r.ownerDid, r.harborName, link),
    el('span', 'show-cat', r.category.toUpperCase()),
    el('span', 'show-viewers', `${r.segments.length} spot${r.segments.length === 1 ? '' : 's'} · ${mins} min`),
  )
  return meta
}

// Avatar + host name. With `link`, both become a link to the streamer's
// Bluesky profile (watch/replay headers only — inside directory tiles the
// whole tile is already a button).
function hostChip(ownerDid: string, fallback: string, link: boolean): HTMLElement[] {
  const p = profiles.get(ownerDid)
  const parts: HTMLElement[] = []
  let avatar: HTMLElement | null = null
  if (p?.avatar) {
    const img = el('img', 'show-avatar') as HTMLImageElement
    img.src = p.avatar
    img.alt = ''
    avatar = img
  }
  const name = el('span', 'show-host', p ? `@${p.handle}` : fallback)
  if (link) {
    const a = el('a', 'show-host-link') as HTMLAnchorElement
    a.href = `https://bsky.app/profile/${p?.handle ?? ownerDid}`
    a.target = '_blank'
    a.rel = 'noopener'
    a.title = 'View on Bluesky'
    if (avatar) a.append(avatar)
    a.append(name)
    parts.push(a)
  } else {
    if (avatar) parts.push(avatar)
    parts.push(name)
  }
  return parts
}

// "All shows by this host" — only worth showing when we know their handle.
function hostPageLink(ownerDid: string): HTMLElement | null {
  const p = profiles.get(ownerDid)
  if (!p) return null
  const a = el('a', 'show-chip host-page-link', `📺 @${p.handle}’s shows`) as HTMLAnchorElement
  a.href = `/host/${p.handle}/`
  return a
}

function streamMeta(s: LiveStreamView, link = false): HTMLElement {
  const meta = el('div', 'show-meta')
  meta.append(
    ...hostChip(s.ownerDid, s.harborName, link),
    el('span', 'show-cat', s.category.toUpperCase()),
    el('span', 'show-viewers', `${s.viewers} watching`),
  )
  return meta
}

function cta(label: string): HTMLElement {
  const a = el('a', 'show-cta', label) as HTMLAnchorElement
  a.href = 'https://blueharbor.at'
  return a
}

// ---- watching ----

async function watch(stream: LiveStreamView): Promise<void> {
  const res = await fetch(
    `${API}/api/watch-token?stream=${encodeURIComponent(stream.id)}${
      REC_KEY ? `&rk=${encodeURIComponent(REC_KEY)}` : ''
    }`,
  )
  if (!res.ok) {
    void refresh()
    return
  }
  // Every watch session is shareable/bookmarkable.
  history.replaceState(null, '', `${basePath()}?watch=${encodeURIComponent(stream.id)}`)
  const { token, url } = (await res.json()) as { token: string; url: string }

  app.replaceChildren()
  const view = el('div', 'watch-view')
  const bar = el('div', 'watch-bar')
  const back = el('button', 'show-chip', '← All channels')
  back.onclick = () => stopWatching()
  const reportBtn = el('button', 'show-chip show-report', '⚠ Report')
  reportBtn.onclick = async () => {
    const reason = prompt('What’s wrong with this stream? (nudity/sexual content, violence, harassment, other)')
    if (reason === null) return
    reportBtn.disabled = true
    try {
      await fetch(`${API}/api/report`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ stream: stream.id, reason }),
      })
      reportBtn.textContent = '✓ Reported'
    } catch {
      reportBtn.disabled = false
    }
  }
  bar.append(
    back,
    el('span', 'live-pill', '🔴 LIVE'),
    el('h2', undefined, stream.title),
    streamMeta(stream, true),
    ...(hostDid ? [] : [hostPageLink(stream.ownerDid)].filter((n): n is HTMLElement => n !== null)),
    ...shareChips(`https://blueskyshow.com/?watch=${stream.id}`, `🔴 Live now: ${stream.title}`),
    reportBtn,
    cta('Join on BlueHarbor'),
  )
  const statusBox = statusEl(stream.statusText)
  const stageWrap = el('div', 'watch-stage-wrap')
  const grid = el('div', 'watch-grid')
  const waiting = el('div', 'watch-waiting')
  // Standby card — the channel's own art, or the network's as a fallback —
  // instead of a black void while nobody is on camera.
  const standby = el('img', 'watch-standby') as HTMLImageElement
  standby.src = standbyFor(stream)
  standby.alt = stream.title
  waiting.append(
    standby,
    el(
      'p',
      undefined,
      stream.standbyImg
        ? 'On the air — no camera right now.'
        : 'On the air — no camera right now. Audio only.',
    ),
  )
  const updateWaiting = () => {
    waiting.style.display = grid.querySelector('video') ? 'none' : ''
  }
  stageWrap.append(waiting, grid)
  // The host's graphics package, switched live through room metadata.
  const overlayImg = el('img', 'video-overlay') as HTMLImageElement
  overlayImg.alt = ''
  overlayImg.style.display = 'none'
  stageWrap.append(overlayImg)
  let overlayFade = 400
  let overlayScale = 100
  const applyOverlay = (metadata: string | undefined) => {
    let url: string | null = null
    try {
      const o = metadata
        ? ((
            JSON.parse(metadata) as {
              overlay?: string | { u: string; f?: number; s?: number } | null
            }
          ).overlay ?? null)
        : null
      if (typeof o === 'string') url = o
      else if (o) {
        url = o.u
        overlayFade = o.f ?? 400
        overlayScale = o.s ?? 100
      }
    } catch {
      url = null
    }
    overlayImg.style.transition = `opacity ${overlayFade}ms ease`
    overlayImg.style.width = `${overlayScale}%`
    overlayImg.style.height = `${overlayScale}%`
    overlayImg.style.margin = 'auto'
    if (url) {
      const next = `${API}${url}`
      if (overlayImg.getAttribute('src') !== next) {
        overlayImg.style.opacity = '0'
        overlayImg.src = next
      }
      overlayImg.style.display = ''
      requestAnimationFrame(() => requestAnimationFrame(() => (overlayImg.style.opacity = '1')))
    } else if (overlayImg.getAttribute('src')) {
      // Fade out at the same pace, then drop it.
      overlayImg.style.opacity = '0'
      setTimeout(() => {
        if (overlayImg.style.opacity === '0') {
          overlayImg.removeAttribute('src')
          overlayImg.style.display = 'none'
        }
      }, overlayFade)
    }
  }
  // Name strap. Room metadata carries only the identity; the strap's contents
  // come from that DID's public Bluesky profile, same as the host sees.
  const strap = mountLowerThird(stageWrap)
  const applyLowerThird = (metadata: string | undefined) => {
    let identity: string | null = null
    try {
      identity = metadata ? ((JSON.parse(metadata) as { lt?: string | null }).lt ?? null) : null
    } catch {
      identity = null
    }
    if (!identity) {
      strap.set(null)
      return
    }
    const named = identity
    const did = didOfIdentity(named)
    strap.set(named, profiles.get(did))
    if (!profiles.has(did)) {
      void loadProfiles([did]).then(() => strap.set(named, profiles.get(did)))
    }
  }

  view.append(bar, ...(statusBox ? [statusBox] : []), stageWrap)
  const reactions = mountReactions(stageWrap)
  // Which video is the reaction for? The program cut if there is one, else
  // the only tile on screen.
  const reactionTarget = (): string | undefined => {
    const tiles = [...grid.querySelectorAll<HTMLElement>('.watch-tile')]
    const programTile = tiles.find((t) => t.classList.contains('program-tile'))
    return (programTile ?? (tiles.length === 1 ? tiles[0] : undefined))?.dataset.id
  }
  const tileFor_ = (id?: string): HTMLElement | null =>
    id ? grid.querySelector<HTMLElement>(`[data-id="${CSS.escape(id)}"]`) : null
  if (!REC_MODE) {
    const send = makeSender((emoji, count) => {
      const target = reactionTarget()
      // Local echo lands on the same tile the relay will name.
      reactions.spawn(emoji, count, tileFor_(target))
      void fetch(`${API}/api/react`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ stream: stream.id, emoji, count, target }),
      }).catch(() => {})
    })
    view.append(reactionBar(send))
  }
  app.append(view)

  const room = new Room({ adaptiveStream: true })
  watching = room

  const tileFor = (identity: string): HTMLElement => {
    let tile = grid.querySelector<HTMLElement>(`[data-id="${CSS.escape(identity)}"]`)
    if (!tile) {
      tile = el('div', 'watch-tile')
      tile.dataset.id = identity
      const did = didOfIdentity(identity)
      const label = el('span', 'call-name', did.startsWith('did:') ? did.slice(0, 24) : did)
      void loadProfiles([did]).then(() => {
        const p = profiles.get(did)
        if (p) label.textContent = p.displayName || `@${p.handle}`
      })
      tile.append(label)
      grid.append(tile)
    }
    return tile
  }

  // Multi-cam: the streamer's switcher stores the program camera's identity
  // in room metadata. When it points at a camera we can actually see, show
  // only that tile; otherwise fall back to the grid of everyone.
  let program: string | null = null
  let pip: string | null = null
  let gains: Record<string, number> = {}
  const parseProgram = (metadata: string | undefined): string | null => {
    if (!metadata) return null
    try {
      return (JSON.parse(metadata) as { program?: string | null }).program ?? null
    } catch {
      return null
    }
  }
  const parsePip = (metadata: string | undefined): string | null => {
    if (!metadata) return null
    try {
      return (JSON.parse(metadata) as { pip?: string | null }).pip ?? null
    } catch {
      return null
    }
  }
  const parseGains = (metadata: string | undefined): Record<string, number> => {
    if (!metadata) return {}
    try {
      return (JSON.parse(metadata) as { gains?: Record<string, number> }).gains ?? {}
    } catch {
      return {}
    }
  }
  // The owner's mix applies to the public audience too.
  const applyGains = () => {
    grid.querySelectorAll<HTMLAudioElement>('audio').forEach((a) => {
      const id = a.closest<HTMLElement>('.watch-tile')?.dataset.id
      if (id) a.volume = gains[id] ?? 1
      applyMute(a)
    })
  }
  const applyProgram = () => {
    const tiles = grid.querySelectorAll<HTMLElement>('.watch-tile')
    let programTile: HTMLElement | null = null
    if (program) {
      for (const t of tiles) {
        if (t.dataset.id === program && t.querySelector('video')) programTile = t
      }
    }
    for (const t of tiles) {
      t.classList.toggle('program-tile', t === programTile)
      // Talk-over: this one rides in the corner instead of being hidden.
      t.classList.toggle('pip-tile', Boolean(pip) && t.dataset.id === pip && t !== programTile)
    }
    grid.classList.toggle('has-program', programTile !== null)
  }

  room.on(RoomEvent.RoomMetadataChanged, (metadata) => {
    program = parseProgram(metadata)
    pip = parsePip(metadata)
    gains = parseGains(metadata)
    applyProgram()
    applyGains()
    applyOverlay(metadata)
    applyLowerThird(metadata)
  })
  room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
    try {
      const msg = JSON.parse(new TextDecoder().decode(payload)) as { t?: string; e?: string; n?: number; id?: string }
      if (msg.t === 'react' && msg.e) reactions.spawn(msg.e, msg.n ?? 1, tileFor_(msg.id))
    } catch {
      // not ours
    }
  })
  room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, pub, participant: RemoteParticipant) => {
    // Screen shares are a separate source: their own tile, cuttable like a
    // camera, rather than covering the person's face.
    const isScreen = pub?.source === Track.Source.ScreenShare
    const tile = tileFor(isScreen ? `${participant.identity}#screen` : participant.identity)
    if (track.kind === Track.Kind.Video) {
      const v = track.attach()
      v.autoplay = true
      ;(v as HTMLVideoElement).playsInline = true
      applyMute(v)
      tile.prepend(v)
    } else {
      const a = track.attach() as HTMLAudioElement
      a.style.display = 'none'
      a.volume = gains[participant.identity] ?? 1
      applyMute(a)
      tile.append(a)
      tile.classList.add('audio-only')
      tile.style.backgroundImage = `url(${standbyFor(stream)})`
    }
    applyProgram()
    updateWaiting()
  })
  room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
    track.detach().forEach((n) => n.remove())
    applyProgram()
    updateWaiting()
  })
  room.on(RoomEvent.ParticipantDisconnected, (p) => {
    grid.querySelector(`[data-id="${CSS.escape(p.identity)}"]`)?.remove()
    applyProgram()
    updateWaiting()
  })
  room.on(RoomEvent.Disconnected, () => {
    reactions.destroy()
    strap.destroy()
    if (watching === room) stopWatching()
  })

  try {
    await room.connect(url, token)
    // Late joiner: the cut (and mix) may already be in place.
    program = parseProgram(room.metadata)
    pip = parsePip(room.metadata)
    gains = parseGains(room.metadata)
    applyProgram()
    applyGains()
    applyOverlay(room.metadata)
    applyLowerThird(room.metadata)
  } catch {
    stopWatching()
  }
}

function stopWatching(): void {
  const room = watching
  watching = null
  replayActive = false
  if (room) void room.disconnect().catch(() => {})
  replayCleanup?.()
  replayCleanup = null
  history.replaceState(null, '', basePath())
  render()
  void refresh()
}

// ---- replays (today's reels) ----

function watchReplay(show: ReplayShow): void {
  replayCleanup?.()
  replayActive = true
  history.replaceState(null, '', `${basePath()}?watch=${encodeURIComponent(show.id)}`)
  app.replaceChildren()
  const view = el('div', 'watch-view')
  const bar = el('div', 'watch-bar')
  const back = el('button', 'show-chip', '← All channels')
  back.onclick = () => stopWatching()
  bar.append(
    back,
    el('span', 'live-pill replay-pill', 'REPLAY'),
    el('h2', undefined, show.title),
    replayMeta(show, true),
    ...(hostDid ? [] : [hostPageLink(show.ownerDid)].filter((n): n is HTMLElement => n !== null)),
    ...shareChips(`https://blueskyshow.com/?watch=${show.id}`, `📺 ${show.title} — today on The Bluesky Show`),
    cta('Join on BlueHarbor'),
  )
  const stage = el('div', 'replay-stage')
  const video = el('video', 'replay-video') as HTMLVideoElement
  video.controls = !REC_MODE
  video.autoplay = true
  video.playsInline = true
  if (show.poster) video.poster = `${API}${show.poster}`
  const progress = el('div', 'replay-progress')

  // Spot strip: one chip per recording, click to jump.
  const spots = el('div', 'replay-spots')
  const chips: HTMLButtonElement[] = show.segments.map((seg, i) => {
    const when = new Date(seg.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    const chip = el('button', 'show-chip replay-spot', `${i + 1} · ${seg.title} (${when})`) as HTMLButtonElement
    chip.onclick = () => {
      index = i
      void playCurrent()
    }
    return chip
  })
  if (show.segments.length > 1) spots.append(...chips)

  stage.append(video)
  const replayStatus = statusEl(show.statusText)
  view.append(bar, ...(replayStatus ? [replayStatus] : []), stage, spots, progress)
  app.append(view)

  // The day's spots play back-to-back as one show.
  let index = 0
  let hls: { destroy(): void } | null = null
  let disposed = false
  const native = video.canPlayType('application/vnd.apple.mpegurl') !== ''

  const playCurrent = async (): Promise<void> => {
    if (disposed) return
    if (index >= show.segments.length) {
      progress.textContent = 'That’s the whole show — replays clear at the nightly sign-off.'
      chips.forEach((c) => c.classList.remove('active'))
      return
    }
    const seg = show.segments[index]
    chips.forEach((c, i) => c.classList.toggle('active', i === index))
    progress.textContent =
      show.segments.length > 1
        ? `Spot ${index + 1} of ${show.segments.length} — ${seg.title} (${new Date(seg.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })})`
        : ''
    const src = `${API}${seg.url}`
    if (native) {
      video.src = src
      void video.play().catch(() => {})
    } else {
      const { default: Hls } = await import('hls.js')
      if (disposed) return
      hls?.destroy()
      const h = new Hls()
      hls = h
      h.loadSource(src)
      h.attachMedia(video)
      h.on(Hls.Events.MANIFEST_PARSED, () => void video.play().catch(() => {}))
      // A broken spot skips forward instead of silently dying.
      h.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal && !disposed) {
          h.destroy()
          index += 1
          void playCurrent()
        }
      })
    }
  }
  const onEnded = () => {
    index += 1
    void playCurrent()
  }
  const onError = () => {
    // Native playback error (Safari): skip to the next spot.
    if (!disposed && video.error && index < show.segments.length) {
      index += 1
      void playCurrent()
    }
  }
  video.addEventListener('ended', onEnded)
  video.addEventListener('error', onError)
  replayCleanup = () => {
    disposed = true
    video.removeEventListener('ended', onEnded)
    video.removeEventListener('error', onError)
    hls?.destroy()
    hls = null
    video.src = ''
  }
  void playCurrent()
}

void refresh()
setInterval(() => {
  if (!document.hidden && !watching && !replayActive) void refresh()
}, 20_000)
